import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Users, UserX, UserCheck, Zap, BarChart2 } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import InfoTile from "../../../components/common/InfoTile";
import PrimaryCell from "../../../components/common/PrimaryCell";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import {
  useGetCollectorOptionsQuery,
  useGetCollectorStatsQuery,
  useListCollectorAllocationsQuery,
  useAutofillCollectorAllocationsMutation,
  useSetCollectorAllocationsMutation,
  useCanManageCollectorAllocation,
  useCanViewCollectorPerformance,
  money, tidy, customerKey, sinceLabel,
} from "../collectorListsApi";
import { useLazySearchEmployeesQuery } from "../../users/employeesApi";
import { paths } from "../../../routes/paths";

/*
  Collector Lists (Batch189) - which collector each customer belongs to.

  The list is what the Collector Performance report measures against, so the
  rules are strict and they are the user's: every customer on a list is
  expected EVERY month, and a customer has ONE collector at a time. Moving a
  customer does not rewrite history - the old link is ended and a new one
  starts, so past months still count for whoever owned the customer then.

  The rows are every customer that is on a list now OR was collected in the
  last 12 months. That second half is what makes "customers with no collector"
  a real figure: the customer master holds thousands of long-dead accounts, and
  counting those would bury the ones that actually pay.
*/

const selectStyles = {
  container: (base) => ({ ...base, minWidth: 300 }),
  control: (base) => ({ ...base, minHeight: 34 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

const monthsSince = (date) => (date ? Math.max(0, dayjs().startOf("month").diff(dayjs(date).startOf("month"), "month")) : null);

const AutofillModal = ({ show, onClose, branchId }) => {
  const [months, setMonths] = useState(12);
  const [effectiveFrom, setEffectiveFrom] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  // The preview is only good for the inputs it was run with - Apply stays
  // locked until a preview exists for exactly what is on screen.
  const [preview, setPreview] = useState(null);
  const [autofill, { isLoading }] = useAutofillCollectorAllocationsMutation();

  useEffect(() => {
    if (show) setPreview(null);
  }, [show]);

  const inputKey = `${months}|${effectiveFrom}|${branchId ?? ""}`;
  const previewIsCurrent = preview?.key === inputKey;

  const run = async (isPreview) => {
    try {
      const result = await autofill({
        branchId: branchId ?? undefined, months: Number(months) || 12, effectiveFrom, preview: isPreview,
      }).unwrap();
      if (isPreview) return setPreview({ key: inputKey, ...result });
      toast.success(`${result.wouldAllocate} customer(s) added to collector lists.`);
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not run the auto-fill");
    }
    return null;
  };

  return (
    <Modal show={show} onHide={isLoading ? undefined : onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Auto-fill from history</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          Every customer that <strong>nobody owns yet</strong> is given to the one collector who has been collecting
          from them. Customers already on a list are never touched.
        </p>
        <p className="text-muted small">
          Where several people collected a customer, it goes to whoever did at least 70% of the collections;
          anything less clear is left alone for you to decide. Mombasa also uses the old system&rsquo;s history.
        </p>
        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label">Look back (months)</label>
            <input type="number" min="1" max="24" className="form-control" value={months} onChange={(e) => setMonths(e.target.value)} />
          </div>
          <div className="col-6 mb-3">
            <label className="form-label">Count from</label>
            <input type="date" className="form-control" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            <small className="text-muted">The report judges collectors from this date.</small>
          </div>
        </div>
        {previewIsCurrent && (
          <div className="alert alert-info py-2 mb-0">
            Would add <strong>{preview.wouldAllocate}</strong> customer(s) to lists.{" "}
            <strong>{preview.leftForReview}</strong> were collected by more than one person and are left for you to decide.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button variant="outline-primary" onClick={() => run(true)} disabled={isLoading}>Preview</Button>
        <Button variant="primary" onClick={() => run(false)} disabled={isLoading || !previewIsCurrent || !preview.wouldAllocate}>
          {isLoading ? "Working…" : "Apply"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const CollectorListsPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const canManage = useCanManageCollectorAllocation();
  const canViewPerformance = useCanViewCollectorPerformance();
  const [searchParams] = useSearchParams();

  const [collector, setCollector] = useState(null);
  // ?unallocated=1 is how the performance report's "no collector" tile lands here.
  const [unallocatedOnly, setUnallocatedOnly] = useState(searchParams.get("unallocated") === "1");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [moveTo, setMoveTo] = useState(null);
  const [moveFrom, setMoveFrom] = useState(dayjs().format("YYYY-MM-DD"));
  const [showAutofill, setShowAutofill] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const branchId = selectedBranchId ?? undefined;
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  const { data: options = [] } = useGetCollectorOptionsQuery({ branchId });
  const { data: stats } = useGetCollectorStatsQuery({ branchId });
  const { data: rows = [], isFetching, isError, error } = useListCollectorAllocationsQuery({
    branchId,
    collector: unallocatedOnly ? undefined : collector?.value,
    unallocatedOnly: unallocatedOnly || undefined,
    search: search || undefined,
  });
  const [setAllocations, { isLoading: isSaving }] = useSetCollectorAllocationsMutation();

  const collectorOptions = useMemo(
    () => options.map((o) => ({
      value: o.employeeRecordGuid,
      label: `${tidy(o.empName)} · ${o.empCode} · ${o.branchCode} · ${o.customersOnList} customer${o.customersOnList === 1 ? "" : "s"}`,
    })),
    [options],
  );

  // "Move to" searches EVERY employee (2026-09-22: "there are many who collect
  // from different customers than collectors designation"), through the same
  // /employees/search the Employee picker uses everywhere else. The existing
  // collectors are offered first, before anything is typed; typing a name or
  // code brings up anyone, and an employee already holding a list keeps their
  // customer count in the label so the two kinds are told apart.
  const [searchEmployees] = useLazySearchEmployeesQuery();
  const customersOnListByGuid = useMemo(
    () => new Map(options.map((o) => [o.employeeRecordGuid, o.customersOnList])),
    [options],
  );
  const loadMoveToOptions = async (inputValue) => {
    const q = (inputValue || "").trim();
    if (!q) return collectorOptions;
    const { data = [] } = await searchEmployees(q);
    return data.map((emp) => {
      const onList = customersOnListByGuid.get(emp.recordGuid);
      const name = tidy(`${emp.empName || ""} ${emp.empLastName || ""}`);
      return {
        value: emp.recordGuid,
        label: onList != null
          ? `${name} · ${emp.empCode} · ${onList} customer${onList === 1 ? "" : "s"}`
          : `${name} · ${emp.empCode} · not a collector yet`,
      };
    });
  };

  // A selection made under one filter must not silently act on rows that are
  // no longer on screen.
  useEffect(() => {
    setSelectedKeys([]);
  }, [branchId, collector, unallocatedOnly, search]);

  const selectedRows = useMemo(() => {
    const keys = new Set(selectedKeys);
    return rows.filter((r) => keys.has(customerKey(r)));
  }, [rows, selectedKeys]);

  const save = async (collectorGuid) => {
    try {
      await setAllocations({
        customers: selectedRows.map((r) => ({ customerBranchId: r.customerBranchId, custId: r.custId })),
        collectorEmployeeRecordGuid: collectorGuid,
        effectiveFrom: moveFrom || undefined,
      }).unwrap();
      toast.success(collectorGuid ? `${selectedRows.length} customer(s) moved.` : `${selectedRows.length} customer(s) taken off their list.`);
      setSelectedKeys([]);
      setMoveTo(null);
    } catch (e) {
      toast.error(e?.message || "Could not save the change");
    }
  };

  const handleRemove = () => {
    if (window.confirm(`Take ${selectedRows.length} customer(s) off their collector's list? Past months stay as they were.`)) save(null);
  };

  const columns = [
    {
      title: "Customer",
      dataIndex: "custName",
      render: (v, r) => <PrimaryCell primary={tidy(v) || `Customer ${r.custId}`} secondary={`#${r.custId} · ${r.customerBranchCode}`} />,
    },
    {
      title: "Collector",
      dataIndex: "collectorName",
      render: (v, r) =>
        r.collectorEmployeeRecordGuid ? (
          <>
            <div>{tidy(v)}</div>
            <div className="text-muted small">Code {r.collectorEmpCode}</div>
          </>
        ) : (
          <span className="badge badge-linedanger">No collector</span>
        ),
    },
    {
      title: "On list since",
      dataIndex: "allocatedFrom",
      render: (v, r) =>
        v ? (
          <>
            {dayjs(v).format("DD-MMM-YYYY")}
            <span className="text-muted small ms-1">{r.allocationSource === "AUTO" ? "auto" : "manual"}</span>
          </>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      title: "Last collected",
      dataIndex: "lastCollectedDate",
      render: (v, r) => {
        const m = monthsSince(v);
        return (
          <>
            <div>{v ? dayjs(v).format("DD-MMM-YYYY") : <span className="text-muted">never</span>}</div>
            <div className={`small ${m == null || m > 1 ? "text-danger" : "text-muted"}`}>{sinceLabel(m)}</div>
            {r.collectors12m > 1 && (
              <div className="small" style={{ color: "#B7791F" }}>collected by {r.collectors12m} people</div>
            )}
          </>
        );
      },
    },
    { title: "Collections (12 m)", dataIndex: "collections12m", align: "right" },
    { title: "Amount (12 m)", dataIndex: "amount12m", align: "right", render: (v) => money(v) },
  ];

  const exportColumns = [
    { header: "Customer", value: (r) => tidy(r.custName) },
    { header: "Cust No.", value: (r) => r.custId },
    { header: "Branch", value: (r) => r.customerBranchCode },
    { header: "Collector", value: (r) => tidy(r.collectorName) || "No collector" },
    { header: "Collector Code", value: (r) => r.collectorEmpCode ?? "" },
    { header: "On list since", value: (r) => (r.allocatedFrom ? dayjs(r.allocatedFrom).format("DD-MMM-YYYY") : "") },
    { header: "Last collected", value: (r) => (r.lastCollectedDate ? dayjs(r.lastCollectedDate).format("DD-MMM-YYYY") : "never") },
    { header: "Collections (12 m)", value: (r) => r.collections12m, align: "right" },
    { header: "Amount (12 m)", value: (r) => Number(r.amount12m || 0), align: "right" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Collector Lists</h4>
              <h6>Which collector each customer belongs to. Every customer on a list is expected every month.</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            {canViewPerformance && (
              <Link to={paths.collectorPerformance} className="btn btn-outline-secondary d-inline-flex align-items-center gap-1">
                <BarChart2 size={16} /> Performance report
              </Link>
            )}
            {canManage && (
              <button type="button" className="btn btn-added d-inline-flex align-items-center gap-1" onClick={() => setShowAutofill(true)}>
                <Zap size={16} /> Auto-fill from history
              </button>
            )}
          </div>
        </div>

        {!canManage && <div className="alert alert-info py-2">You can view the lists but not change them.</div>}

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-4">
            <InfoTile label="Customers on a list" tone="success" value={stats?.allocatedCustomers ?? "—"} />
          </div>
          <div className="col-6 col-md-4">
            <InfoTile label="Collectors with a list" value={stats?.collectorsWithList ?? "—"} />
          </div>
          <div className="col-12 col-md-4">
            <InfoTile
              label="Customers with no collector"
              tone={stats?.unallocatedCustomers ? "maroon" : "neutral"}
              value={stats?.unallocatedCustomers ?? "—"}
              active={unallocatedOnly}
              onClick={() => setUnallocatedOnly((v) => !v)}
              title="Collected from in the last 12 months, but on nobody's list. Click to show only these."
            />
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body d-flex flex-column gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Select
                classNamePrefix="react-select"
                styles={selectStyles}
                isClearable
                isDisabled={unallocatedOnly}
                options={collectorOptions}
                value={unallocatedOnly ? null : collector}
                onChange={setCollector}
                placeholder="All collectors - type to search"
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 260, height: 34 }}
                placeholder="Customer name or number"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <div className="form-check form-switch ms-1 mb-0">
                <input
                  className="form-check-input" type="checkbox" id="collectorUnallocatedOnly"
                  checked={unallocatedOnly} onChange={(e) => setUnallocatedOnly(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="collectorUnallocatedOnly">No collector only</label>
              </div>
              <span className="text-muted small ms-auto">{rows.length} customer(s)</span>
              <ListExportToolbar
                title="Collector Lists"
                subtitleLines={[
                  `Branch: ${branchCode || "All"}`,
                  unallocatedOnly ? "Customers with no collector" : collector ? `Collector: ${collector.label}` : null,
                  search ? `Search: "${search}"` : null,
                ].filter(Boolean)}
                branchCode={branchCode}
                columns={exportColumns}
                rows={rows}
                fileName="CollectorLists"
              />
            </div>

            {canManage && selectedRows.length > 0 && (
              <div className="d-flex flex-wrap align-items-center gap-2 p-2 rounded" style={{ background: "#F7ECEE", border: "1px solid #E7C9CF" }}>
                <strong className="d-inline-flex align-items-center gap-1"><Users size={15} /> {selectedRows.length} selected</strong>
                <AsyncSelect
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  isClearable
                  defaultOptions={collectorOptions}
                  loadOptions={loadMoveToOptions}
                  value={moveTo}
                  onChange={setMoveTo}
                  placeholder="Move to… type any employee's name or code"
                  noOptionsMessage={({ inputValue }) => (inputValue ? "No matching employees" : "Type a name or code")}
                  menuPortalTarget={document.body}
                />
                <label className="small text-muted mb-0">From</label>
                <input type="date" className="form-control form-control-sm" style={{ width: 160, height: 34 }} value={moveFrom} onChange={(e) => setMoveFrom(e.target.value)} />
                <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" disabled={!moveTo || isSaving} onClick={() => save(moveTo.value)}>
                  <UserCheck size={14} /> {isSaving ? "Saving…" : "Move"}
                </button>
                <button type="button" className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1" disabled={isSaving} onClick={handleRemove}>
                  <UserX size={14} /> Take off list
                </button>
              </div>
            )}

            {isError && <div className="alert alert-danger py-2 mb-0">Could not load the lists{error?.message ? `: ${error.message}` : "."}</div>}

            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={rows}
                rowKey={customerKey}
                loading={isFetching}
                rowSelection={canManage ? { selectedRowKeys: selectedKeys, onChange: setSelectedKeys, preserveSelectedRowKeys: false } : undefined}
              />
            </div>
            <small className="text-muted">
              Shown: every customer on a list today, plus every customer collected from in the last 12 months.
              Moving a customer keeps past months with the collector who had them then.
            </small>
          </div>
        </div>
      </div>

      <AutofillModal show={showAutofill} onClose={() => setShowAutofill(false)} branchId={selectedBranchId} />
    </div>
  );
};

export default CollectorListsPage;
